import { fundingSDOptions, projectionSDOptions, getUSANumberFormat } from "app/utils/utils";
import type { ColumnDef, ChartDataset } from "./IRCFundingPage";

export interface LedgerRow {
  insured?: { name?: string };
  insuranceCompany?: { name?: string } | null;
  policyNumber?: string;
  type?: string;
  benefit?: number | string;
  expiration?: number;
  method?: string;
  mode?: string;
  amount?: number | string;
  annualizedPremium?: number | string;
}

const METHOD_ABBR: Record<string, string> = {
  IAPP_FACILITATED: "IF",
  EXTERNALLY_DIRECTED: "ED",
};

const MODE_ABBR: Record<string, string> = {
  MONTHLY: "M",
  QUARTERLY: "Q",
  SEMI_ANNUALLY: "SA",
  ANNUALLY: "A",
};

const toNum = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : parseFloat(String(v ?? "")) || 0;

const fmtCompact = (value: number | string): string => {
  const v = toNum(value);
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${v.toLocaleString("en-US")}`;
};

const fmtCurrency = (value: number | string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toNum(value));

export interface LedgerPageData {
  rows: LedgerRow[];
  columns: ColumnDef[];
  totalBenefit: number;
  totalAnnualizedPremium: number;
}

export function adaptLedger(
  ledger:
    | { rows?: LedgerRow[]; totalBenefit?: number; totalAnnualizedPremium?: number }
    | null
    | undefined,
): LedgerPageData | null {
  if (!ledger?.rows?.length) return null;

  const columns: ColumnDef[] = [
    { label: "Insured", cell: (r: LedgerRow) => r.insured?.name ?? "—" },
    { label: "Insurer", cell: (r: LedgerRow) => r.insuranceCompany?.name ?? "—" },
    { label: "Policy #", cell: (r: LedgerRow) => r.policyNumber ?? "—" },
    { label: "Type", cell: (r: LedgerRow) => r.type ?? "—" },
    {
      label: "Benefit",
      cell: (r: LedgerRow) => fmtCompact(r.benefit ?? 0),
      total: (rows: LedgerRow[]) => fmtCompact(rows.reduce((s, r) => s + toNum(r.benefit), 0)),
    },
    {
      label: "Expiration",
      cell: (r: LedgerRow) =>
        r.expiration != null ? `${r.expiration} Yr${r.expiration === 1 ? "" : "s"}` : "—",
    },
    {
      label: "Method",
      cell: (r: LedgerRow) => METHOD_ABBR[r.method ?? ""] ?? r.method ?? "—",
    },
    {
      label: "Mode",
      cell: (r: LedgerRow) => MODE_ABBR[r.mode ?? ""] ?? r.mode ?? "—",
    },
    {
      label: "Amount",
      cell: (r: LedgerRow) => fmtCurrency(r.amount ?? 0),
    },
    {
      label: "Annualized",
      cell: (r: LedgerRow) => fmtCurrency(r.annualizedPremium ?? 0),
      total: (rows: LedgerRow[]) =>
        fmtCurrency(rows.reduce((s, r) => s + toNum(r.annualizedPremium), 0)),
    },
  ];

  const benefitTotal = ledger.totalBenefit ?? ledger.rows.reduce((s, r) => s + toNum(r.benefit), 0);
  const annualizedTotal =
    ledger.totalAnnualizedPremium ??
    ledger.rows.reduce((s, r) => s + toNum(r.annualizedPremium), 0);

  return {
    rows: ledger.rows,
    columns,
    totalBenefit: benefitTotal,
    totalAnnualizedPremium: annualizedTotal,
  };
}

const statusStyle = (s: string | null): React.CSSProperties => {
  if (s === "PAID") return { color: "#166534", fontWeight: 600 };
  if (s === "PENDING") return { color: "#92400e" };
  return { color: "#6b7280" };
};

const fmt = (val: number) => `$${getUSANumberFormat(val, false)}`;
const sum = (rows: any[], key: string) => rows.reduce((s: number, r: any) => s + (r[key] ?? 0), 0);

// ─── Premium Finance ───────────────────────────────────────────────────────────

interface PFFundingRow {
  planYear: number;
  actualClientPremium: number;
  actualLenderPremium: number;
  actualInterestPayment: number;
  actualPlanFee: number;
  invoiceStatus: {
    planFee: string | null;
    interest: string | null;
    clientPremium: string | null;
    lenderPremium: string | null;
  };
}

export function adaptPFFunding(
  funding: PFFundingRow[],
  primaryColor: string,
): {
  rows: PFFundingRow[];
  columns: ColumnDef[];
  chartLabels: (string | number)[];
  chartDatasets: ChartDataset[];
} {
  const colors = fundingSDOptions(primaryColor);
  const data = funding.slice(0, 10);

  const columns: ColumnDef[] = [
    { label: "Plan Year", cell: (r) => r.planYear },
    {
      label: "Client Premium",
      cell: (r) => fmt(r.actualClientPremium),
      total: (rows) => fmt(sum(rows, "actualClientPremium")),
    },
    {
      label: "Lender Premium",
      cell: (r) => fmt(r.actualLenderPremium),
      total: (rows) => fmt(sum(rows, "actualLenderPremium")),
    },
    {
      label: "Interest Payment",
      cell: (r) => fmt(r.actualInterestPayment),
      total: (rows) => fmt(sum(rows, "actualInterestPayment")),
    },
    {
      label: "Plan Fee",
      cell: (r) => fmt(r.actualPlanFee),
      total: (rows) => fmt(sum(rows, "actualPlanFee")),
    },
    {
      label: "Client Status",
      cell: (r) => r.invoiceStatus?.clientPremium ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.clientPremium),
    },
    {
      label: "Interest Status",
      cell: (r) => r.invoiceStatus?.interest ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.interest),
    },
    {
      label: "Plan Fee Status",
      cell: (r) => r.invoiceStatus?.planFee ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.planFee),
    },
  ];

  const chartDatasets: ChartDataset[] = [
    {
      backgroundColor: colors[0].indicatorColor,
      data: data.map((r: PFFundingRow) => r.actualClientPremium),
      label: "Client Premium",
    },
    {
      backgroundColor: colors[1].indicatorColor,
      data: data.map((r: PFFundingRow) => r.actualLenderPremium),
      label: "Lender Premium",
    },
    {
      backgroundColor: colors[2].indicatorColor,
      data: data.map((r: PFFundingRow) => r.actualInterestPayment),
      label: "Interest",
    },
    {
      backgroundColor: colors[3].indicatorColor,
      data: data.map((r: PFFundingRow) => r.actualPlanFee),
      label: "Plan Fee",
    },
  ];

  return {
    rows: data,
    columns,
    chartLabels: data.map((r: PFFundingRow) => r.planYear),
    chartDatasets,
  };
}

// ─── Split Dollar Funding ─────────────────────────────────────────────────────

interface SDFundingRow {
  planYear: number;
  actualClientPremium: number;
  actualLenderPremium: number;
  actualInterestPayment: number;
  actualPlanFee: number;
  invoiceStatus: {
    planFee: string | null;
    interest: string | null;
    clientPremium: string | null;
    lenderPremium: string | null;
  };
}

export function adaptSDFunding(
  funding: any[],
  primaryColor: string,
): {
  rows: SDFundingRow[];
  columns: ColumnDef[];
  chartLabels: (string | number)[];
  chartDatasets: ChartDataset[];
} {
  const colors = fundingSDOptions(primaryColor);
  const data = funding.slice(0, 10);

  const columns: ColumnDef[] = [
    { label: "Plan Year", cell: (r) => r.planYear },
    {
      label: "Client Premium",
      cell: (r) => fmt(r.actualClientPremium),
      total: (rows) => fmt(sum(rows, "actualClientPremium")),
    },
    {
      label: "Lender Premium",
      cell: (r) => fmt(r.actualLenderPremium),
      total: (rows) => fmt(sum(rows, "actualLenderPremium")),
    },
    {
      label: "Interest Payment",
      cell: (r) => fmt(r.actualInterestPayment),
      total: (rows) => fmt(sum(rows, "actualInterestPayment")),
    },
    {
      label: "Plan Fee",
      cell: (r) => fmt(r.actualPlanFee),
      total: (rows) => fmt(sum(rows, "actualPlanFee")),
    },
    {
      label: "Client Status",
      cell: (r) => r.invoiceStatus?.clientPremium ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.clientPremium),
    },
    {
      label: "Lender Status",
      cell: (r) => r.invoiceStatus?.lenderPremium ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.lenderPremium),
    },
    {
      label: "Interest Status",
      cell: (r) => r.invoiceStatus?.interest ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.interest),
    },
    {
      label: "Plan Fee Status",
      cell: (r) => r.invoiceStatus?.planFee ?? "—",
      cellStyle: (r) => statusStyle(r.invoiceStatus?.planFee),
    },
  ];

  const chartDatasets: ChartDataset[] = [
    {
      backgroundColor: colors[0].indicatorColor,
      data: data.map((r: SDFundingRow) => r.actualClientPremium),
      label: "Client Premium",
    },
    {
      backgroundColor: colors[1].indicatorColor,
      data: data.map((r: SDFundingRow) => r.actualLenderPremium),
      label: "Lender Premium",
    },
    {
      backgroundColor: colors[2].indicatorColor,
      data: data.map((r: SDFundingRow) => r.actualInterestPayment),
      label: "Interest",
    },
    {
      backgroundColor: colors[3].indicatorColor,
      data: data.map((r: SDFundingRow) => r.actualPlanFee),
      label: "Plan Fee",
    },
  ];

  return {
    rows: data,
    columns,
    chartLabels: data.map((r: SDFundingRow) => r.planYear),
    chartDatasets,
  };
}

// ─── BS / KPI Projection Ledger ───────────────────────────────────────────────

interface CoverageMetric {
  current?: number;
  total?: number;
  surplusAmount: number;
  percent: number;
  isSurplus: boolean;
}

export interface ProjectionLedgerRow {
  id: string;
  name: string;
  ownershipPercentage: string;
  profilePicture: string | null;
  exposureValue: number;
  life: CoverageMetric;
  disability: CoverageMetric;
}

export interface ProjectionLedgerCoverage {
  life: CoverageMetric;
  disability: CoverageMetric;
  totalExposureValue: number;
}

export interface ProjectionLedgerEntry {
  planYear: number;
  year: number;
  isCompleted: boolean;
  rows: ProjectionLedgerRow[];
  coverage: ProjectionLedgerCoverage;
}

export function adaptProjectionLedger(
  ledger: ProjectionLedgerEntry[] | null | undefined,
): ProjectionLedgerEntry[] | null {
  if (!ledger?.length) return null;
  return ledger;
}

// ─── BS / KPI Projection ──────────────────────────────────────────────────────

interface BSProjectionGraphRow {
  planYear: number;
  year: number;
  insuredAge: number | null;
  deathBenefit: number;
  disabilityBenefit: number;
  businessValuation: number;
}

interface BSProjectionTableRow {
  planYear: number;
  year: number;
  insuredAge: number | null;
  deathBenefit: number;
  disabilityBenefit: number;
  businessValuation: number;
  annualGrowthRate: number | null;
}

export function adaptBSProjection(
  tableData: BSProjectionTableRow[],
  graphData: BSProjectionGraphRow[],
  primaryColor: string,
): {
  rows: BSProjectionTableRow[];
  columns: ColumnDef[];
  chartLabels: (string | number)[];
  chartDatasets: ChartDataset[];
} {
  const chartData = graphData.slice(0, 10);
  const tableData2 = tableData.slice(0, 10);

  const columns: ColumnDef[] = [
    { label: "Plan Year", cell: (r) => r.planYear },
    {
      label: "Death Benefit",
      cell: (r) => fmt(r.deathBenefit),
      total: (rows) =>
        fmt(rows.reduce((s: number, r: BSProjectionTableRow) => s + (r.deathBenefit ?? 0), 0)),
    },
    {
      label: "Disability Benefit",
      cell: (r) => fmt(r.disabilityBenefit),
      total: (rows) =>
        fmt(rows.reduce((s: number, r: BSProjectionTableRow) => s + (r.disabilityBenefit ?? 0), 0)),
    },
    {
      label: "Shareholder Value",
      cell: (r) => fmt(r.businessValuation),
      total: (rows) =>
        fmt(rows.reduce((s: number, r: BSProjectionTableRow) => s + (r.businessValuation ?? 0), 0)),
    },
    {
      label: "Annual Growth Rate",
      cell: (r) => (r.annualGrowthRate != null ? `${r.annualGrowthRate}%` : "—"),
    },
  ];

  const projColors = projectionSDOptions(primaryColor);
  const chartDatasets: ChartDataset[] = [
    {
      backgroundColor: projColors[3].indicatorColor,
      data: chartData.map((r: BSProjectionGraphRow) => r.deathBenefit),
      label: "Death Benefit",
    },
    {
      backgroundColor: projColors[0].indicatorColor,
      data: chartData.map((r: BSProjectionGraphRow) => r.disabilityBenefit),
      label: "Disability Benefit",
    },
    {
      backgroundColor: projColors[2].indicatorColor,
      data: chartData.map((r: BSProjectionGraphRow) => r.businessValuation),
      label: "Shareholder Value",
    },
  ];

  return {
    rows: tableData2,
    columns,
    chartLabels: chartData.map((r: BSProjectionGraphRow) => r.planYear),
    chartDatasets,
  };
}

// ─── Basic Split (BS) Funding ──────────────────────────────────────────────────

interface BSGraphRow {
  planYear: number;
  year: number;
  insuredAge: number | null;
  lifePolicyPremium: number;
  disabilityPolicyPremium: number;
  adminFees: number;
  planOriginationFees: number;
}

interface BSTableRow {
  planYear: number;
  year: number;
  insuredAge: number | null;
  annualLifePremium: number;
  annualDisabilityPremium: number;
  totalAnnualPremium: number;
  annualAdminFees: number;
  planOriginationFees: number;
  totalPlanCosts: number;
}

export function adaptBSFunding(
  tableData: BSTableRow[],
  graphData: BSGraphRow[],
  primaryColor: string,
): {
  rows: BSTableRow[];
  columns: ColumnDef[];
  chartLabels: (string | number)[];
  chartDatasets: ChartDataset[];
} {
  const colors = fundingSDOptions(primaryColor);
  const chartData = graphData.slice(0, 10);
  const tableData2 = tableData.slice(0, 10);

  const columns: ColumnDef[] = [
    { label: "Plan Year", cell: (r) => r.planYear },
    {
      label: "Life Premium",
      cell: (r) => fmt(r.annualLifePremium),
      total: (rows) => fmt(rows.reduce((s: number, r: BSTableRow) => s + r.annualLifePremium, 0)),
    },
    {
      label: "Disability Premium",
      cell: (r) => fmt(r.annualDisabilityPremium),
      total: (rows) =>
        fmt(rows.reduce((s: number, r: BSTableRow) => s + r.annualDisabilityPremium, 0)),
    },
    {
      label: "Total Annual Premium",
      cell: (r) => fmt(r.totalAnnualPremium),
      total: (rows) => fmt(rows.reduce((s: number, r: BSTableRow) => s + r.totalAnnualPremium, 0)),
    },
    {
      label: "Admin Fees",
      cell: (r) => fmt(r.annualAdminFees),
      total: (rows) => fmt(rows.reduce((s: number, r: BSTableRow) => s + r.annualAdminFees, 0)),
    },
    {
      label: "Plan Origination Fees",
      cell: (r) => fmt(r.planOriginationFees),
      total: (rows) => fmt(rows.reduce((s: number, r: BSTableRow) => s + r.planOriginationFees, 0)),
    },
    {
      label: "Total Plan Costs",
      cell: (r) => fmt(r.totalPlanCosts),
      total: (rows) => fmt(rows.reduce((s: number, r: BSTableRow) => s + r.totalPlanCosts, 0)),
    },
  ];

  const chartDatasets: ChartDataset[] = [
    {
      backgroundColor: colors[0].indicatorColor,
      data: chartData.map((r: BSGraphRow) => r.lifePolicyPremium),
      label: "Life Premium",
    },
    {
      backgroundColor: colors[1].indicatorColor,
      data: chartData.map((r: BSGraphRow) => r.disabilityPolicyPremium),
      label: "Disability Premium",
    },
    {
      backgroundColor: colors[3].indicatorColor,
      data: chartData.map((r: BSGraphRow) => r.adminFees),
      label: "Admin Fees",
    },
  ];

  return {
    rows: tableData2,
    columns,
    chartLabels: chartData.map((r: BSGraphRow) => r.planYear),
    chartDatasets,
  };
}

// ─── BS / KPI Appendix ────────────────────────────────────────────────────────

export interface NormalizedAppendixPolicy {
  policyNumber: string;
  insurerName: string;
  productName: string;
  productType: string;
  mode: string;
  method: string;
  status: string;
  effectiveDate: string;
  deathBenefit: number;
  livingBenefit?: number;
  premiumAmount: number;
  annualPremium: number;
  expirationRemaining: number;
}

export interface BSKPAppendixData {
  lifePolicies: NormalizedAppendixPolicy[];
  disabilityPolicies: NormalizedAppendixPolicy[];
  hasLivingBenefit: boolean;
}

export function adaptBSKPAppendix(appendix: any): BSKPAppendixData | null {
  if (!appendix) return null;

  const normalizePolicy = (p: any): NormalizedAppendixPolicy => ({
    policyNumber: p.policyNumber ?? "—",
    insurerName: p.insuranceCompany?.name ?? "—",
    productName: p.insuranceProductName ?? "—",
    productType: p.insuranceProductType ?? "—",
    mode: MODE_ABBR[p.mode ?? ""] ?? p.mode ?? "—",
    method: METHOD_ABBR[p.method ?? ""] ?? p.method ?? "—",
    status: p.status ?? "—",
    effectiveDate: p.effectiveDate
      ? new Date(p.effectiveDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—",
    deathBenefit: toNum(p.deathBenefit ?? p.benefit),
    livingBenefit: p.livingBenefit != null ? toNum(p.livingBenefit) : undefined,
    premiumAmount: toNum(p.monthlyPremium ?? p.amount),
    annualPremium: toNum(p.annualizedPremium ?? p.annualPremium),
    expirationRemaining: toNum(p.expirationRemaining),
  });

  const lifePolicies = (appendix.lifePolicies ?? []).map(normalizePolicy);
  const disabilityPolicies = (appendix.disabilityPolicies ?? []).map(normalizePolicy);
  const hasLivingBenefit = lifePolicies.some(
    (p: NormalizedAppendixPolicy) => p.livingBenefit != null,
  );

  if (!lifePolicies.length && !disabilityPolicies.length) return null;

  return { lifePolicies, disabilityPolicies, hasLivingBenefit };
}
